import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAppStore } from '../store/appStore';
import { useAuthStore } from '../store/authStore';
import { useRegion } from '../hooks/useRegion';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { DatePicker } from '../components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { Switch } from '../components/ui/switch';
import { formatDate, formatDateDMY, formatRentalPeriod, getStatusColor } from '../lib/utils';
import { Clapperboard, FileText, Download, Plus, Trash2, Loader2, Clock, Edit, Save, FileSpreadsheet, List, Package, Pencil, Check, X, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { getBackendUrl } from '../lib/api';
import {
  computeJobFinancials,
  computeEquipmentTotals,
  getEquipmentItemRate,
  calculatePackageTotal as packageTotal,
  completionDay,
} from '../lib/jobFinancials';
import CrewTab from '../components/CrewTab';
import { PageToolbar } from '../components/PageToolbar';
import { BackButton } from '../components/BackButton';
import { PhoneTitle } from '../components/PhoneHeader';
import { PhonePanel, PHONE_CELL_CONTROL } from '../components/PhonePanel';
import { useIsPhone } from '../hooks/useIsPhone';

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft', color: getStatusColor('draft'), borderColor: 'border-line-strong' },
  { value: 'sent', label: 'Sent', color: getStatusColor('sent'), borderColor: 'border-line-strong' },
  { value: 'booked', label: 'Booked', color: getStatusColor('booked'), borderColor: 'border-inverse' },
  { value: 'lost', label: 'Lost', color: getStatusColor('lost'), borderColor: 'border-brick/50' },
  { value: 'completed', label: 'Completed', color: getStatusColor('completed'), borderColor: 'border-moss/50' },
];

export default function JobDetailPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const {
    currentJob,
    fetchJob,
    updateJob,
    deleteJob,
    duplicateJob,
    daySheets,
    fetchDaySheets,
    createDaySheet,
    updateDaySheet,
    deleteDaySheet,
    downloadQuotePDF,
    downloadInvoicePDF,
    downloadXeroCSV,
    downloadJobEquipmentListPDF,
    settings,
    getXeroStatus,
    sendJobToXero,
    getTreatmentsForJob,
  } = useAppStore();
  const { sessionToken: token, hasProjectsPlan, isAdmin, isDemo, checkProjectsPlanAccess, projectsPlanChecked } = useAuthStore();
  const { formatCurrency, getTaxName, regionConfig, formatDate } = useRegion();

  const [loading, setLoading] = useState(true);
  const [isSheetDialogOpen, setIsSheetDialogOpen] = useState(false);
  const [editingSheetId, setEditingSheetId] = useState(null);
  const [duplicating, setDuplicating] = useState(false);
  const [linkedTreatments, setLinkedTreatments] = useState([]);
  // One tab state for the desktop tabs and the phone panel's
  const [tab, setTab] = useState('overview');
  const isPhone = useIsPhone();
  
  useEffect(() => {
    if (!projectsPlanChecked) {
      checkProjectsPlanAccess();
    }
  }, [projectsPlanChecked, checkProjectsPlanAccess]);

  // Treatments that point at this job (a treatment holds the link; the job doesn't).
  const canSeeTreatments = hasProjectsPlan || isAdmin || isDemo;
  useEffect(() => {
    if (!canSeeTreatments) return undefined;
    let cancelled = false;
    getTreatmentsForJob(jobId)
      .then((list) => { if (!cancelled) setLinkedTreatments(list); })
      .catch(() => { if (!cancelled) setLinkedTreatments([]); });
    return () => { cancelled = true; };
  }, [jobId, canSeeTreatments, getTreatmentsForJob]);

  // Xero state
  const [xeroStatus, setXeroStatus] = useState({ connected: false, tenant_name: null });
  const [sendingToXero, setSendingToXero] = useState(false);
  
  // Lost reason dialog state
  const [lostReasonDialogOpen, setLostReasonDialogOpen] = useState(false);
  const [lostReason, setLostReason] = useState('');
  
  // Helper function to calculate default times based on base hours
  const getDefaultTimes = () => {
    const baseHours = settings?.default_base_hours || 10;
    const callHour = 6; // Default call time is 6am
    // Wrap time = call time + base hours + 1 hour for lunch break
    // e.g., 6am + 10 hours + 1 hour lunch = 5pm
    const wrapHour = callHour + baseHours + 1;
    
    return {
      call_time: `${callHour.toString().padStart(2, '0')}:00`,
      wrap_time: `${wrapHour.toString().padStart(2, '0')}:00`,
    };
  };
  
  const [sheetForm, setSheetForm] = useState({
    date: '',
    phase: 'production',
    label: '',
    call_time: '06:00',
    wrap_time: '17:00',
    breaks: [{ type: 'lunch', out_time: '12:00', in_time: '13:00' }],
  });

  // State for inline editing
  const [editingField, setEditingField] = useState(null); // 'client_job_number' | 'client_job_name' | null
  const [editValue, setEditValue] = useState('');

  // Handler for starting inline edit
  const startEditing = (field, currentValue) => {
    setEditingField(field);
    setEditValue(currentValue || '');
  };

  // Handler for saving inline edit
  const saveInlineEdit = async () => {
    if (!editingField) return;
    
    try {
      await updateJob(jobId, { [editingField]: editValue });
      toast.success('Updated successfully');
      setEditingField(null);
      setEditValue('');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  // Handler for canceling inline edit
  const cancelEditing = () => {
    setEditingField(null);
    setEditValue('');
  };

  // Handler for summary_only toggle
  const handleSummaryOnlyToggle = async (checked) => {
    try {
      await updateJob(jobId, { summary_only: checked });
      toast.success(checked ? 'Summary mode enabled' : 'Summary mode disabled');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  // Helper function to get phase display name with custom label
  const getPhaseDisplayName = (phaseType, daySheetLabel = null) => {
    const baseLabel = phaseType?.replace('_', ' ');
    
    // Prioritize day sheet's own label over phase label
    if (daySheetLabel) {
      return `${baseLabel} | ${daySheetLabel}`;
    }
    
    // Fall back to phase label from job
    if (currentJob?.phases) {
      const phase = currentJob.phases.find(p => p.type === phaseType);
      if (phase?.label) {
        return `${baseLabel} | ${phase.label}`;
      }
    }
    
    return baseLabel;
  };

  useEffect(() => {
    const loadData = async () => {
      // Make all API calls in parallel for faster page load
      const [, , status] = await Promise.all([
        fetchJob(jobId),
        fetchDaySheets(jobId),
        getXeroStatus()
      ]);
      setXeroStatus(status || { connected: false, tenant_name: null });
      setLoading(false);
    };
    loadData();
  }, [jobId, fetchJob, fetchDaySheets, getXeroStatus]);

  const handleStatusChange = async (status) => {
    // If changing to 'lost', open the reason dialog
    if (status === 'lost') {
      setLostReason(currentJob?.lost_reason || '');
      setLostReasonDialogOpen(true);
      return;
    }
    
    try {
      // The server clears lost_reason when a job leaves lost (job_status.py)
      await updateJob(jobId, { status });
      toast.success('Status updated');
      if (status === 'completed' || status === 'paid') {
        toast.info('Equipment ROI updated');
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // The date a completed job was completed: stamped when it's marked
  // completed, and it decides the financial year its money counts in
  const handleCompletedOnChange = async (day) => {
    if (!day) return;
    try {
      await updateJob(jobId, { completed_at: day });
      toast.success('Completion date updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update the completion date');
    }
  };

  const handleLostReasonSubmit = async () => {
    try {
      await updateJob(jobId, { status: 'lost', lost_reason: lostReason });
      toast.success('Job marked as lost');
      setLostReasonDialogOpen(false);
      setLostReason('');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this job? This action cannot be undone.')) {
      try {
        await deleteJob(jobId);
        toast.success('Job deleted');
        navigate('/jobs');
      } catch (error) {
        toast.error('Failed to delete job');
      }
    }
  };

  const handleDuplicate = async () => {
    setDuplicating(true);
    try {
      const newJob = await duplicateJob(jobId);
      toast.success('Job duplicated successfully');
      // Navigate to edit page so user can select new client
      navigate(`/jobs/${newJob.id}/edit`);
    } catch (error) {
      toast.error('Failed to duplicate job');
    } finally {
      setDuplicating(false);
    }
  };

  // Helper to get label from phase
  const getLabelFromPhase = (phaseType) => {
    if (!currentJob?.phases) return '';
    const phase = currentJob.phases.find(p => p.type === phaseType);
    return phase?.label || '';
  };

  const openCreateSheet = () => {
    setEditingSheetId(null);
    const defaultTimes = getDefaultTimes();
    const defaultPhase = 'production';
    setSheetForm({
      date: '',
      phase: defaultPhase,
      label: getLabelFromPhase(defaultPhase), // Pre-fill from phase
      call_time: defaultTimes.call_time,
      wrap_time: defaultTimes.wrap_time,
      breaks: [{ type: 'lunch', out_time: '12:00', in_time: '13:00' }],
    });
    setIsSheetDialogOpen(true);
  };

  const openEditSheet = (sheet) => {
    setEditingSheetId(sheet.id);
    const defaultTimes = getDefaultTimes();
    
    // Use existing breaks or add default lunch if none exist
    let breaks = sheet.breaks || [];
    if (breaks.length === 0) {
      breaks = [{ type: 'lunch', out_time: '12:00', in_time: '13:00' }];
    }
    
    const phaseType = sheet.phase || 'production';
    setSheetForm({
      date: sheet.date || '',
      phase: phaseType,
      label: sheet.label || getLabelFromPhase(phaseType), // Use sheet label or fall back to phase label
      call_time: sheet.call_time || defaultTimes.call_time,
      wrap_time: sheet.wrap_time || defaultTimes.wrap_time,
      breaks: breaks,
    });
    setIsSheetDialogOpen(true);
  };

  const handleSaveDaySheet = async (e) => {
    e.preventDefault();
    try {
      if (editingSheetId) {
        await updateDaySheet(editingSheetId, sheetForm);
        toast.success('Day sheet updated');
      } else {
        await createDaySheet(jobId, sheetForm);
        toast.success('Day sheet created');
      }
      setIsSheetDialogOpen(false);
      setEditingSheetId(null);
    } catch (error) {
      toast.error(editingSheetId ? 'Failed to update day sheet' : 'Failed to create day sheet');
    }
  };

  const handleDeleteDaySheet = async (sheetId) => {
    if (window.confirm('Delete this day sheet?')) {
      try {
        await deleteDaySheet(sheetId);
        toast.success('Day sheet deleted');
        // Refresh job data to reflect the estimate update (bi-directional sync)
        await fetchJob(jobId);
      } catch (error) {
        toast.error('Failed to delete day sheet');
      }
    }
  };

  const addBreak = () => {
    setSheetForm({
      ...sheetForm,
      breaks: [...sheetForm.breaks, { type: 'lunch', out_time: '12:00', in_time: '12:30' }],
    });
  };

  const updateBreak = (index, field, value) => {
    const newBreaks = [...sheetForm.breaks];
    newBreaks[index] = { ...newBreaks[index], [field]: value };
    setSheetForm({ ...sheetForm, breaks: newBreaks });
  };

  const removeBreak = (index) => {
    setSheetForm({
      ...sheetForm,
      breaks: sheetForm.breaks.filter((_, i) => i !== index),
    });
  };

  const handlePhaseChange = (newPhase) => {
    // Update phase and pre-fill label from the job phase
    setSheetForm({
      ...sheetForm,
      phase: newPhase,
      label: getLabelFromPhase(newPhase),
    });
  };

  const handleDownloadEstimate = async () => {
    const toastId = toast.loading('Generating PDF...');
    try {
      await downloadQuotePDF(jobId);
      toast.success('Estimate PDF downloaded', { id: toastId });
    } catch (error) {
      toast.error('Failed to download estimate', { id: toastId });
    }
  };

  const handleDownloadInvoice = async () => {
    const toastId = toast.loading('Generating PDF...');
    try {
      await downloadInvoicePDF(jobId);
      toast.success('Invoice PDF downloaded', { id: toastId });
    } catch (error) {
      toast.error('Failed to download invoice', { id: toastId });
    }
  };

  const handleDownloadXeroCSV = async () => {
    const toastId = toast.loading('Generating Xero CSV...');
    try {
      await downloadXeroCSV(jobId);
      toast.success('Xero CSV downloaded', { id: toastId });
    } catch (error) {
      toast.error('Failed to download Xero CSV', { id: toastId });
    }
  };

  const handleDownloadEquipmentList = async () => {
    const toastId = toast.loading('Generating Equipment List PDF...');
    try {
      await downloadJobEquipmentListPDF(jobId);
      toast.success('Equipment List PDF downloaded', { id: toastId });
    } catch (error) {
      toast.error('Failed to download Equipment List PDF', { id: toastId });
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!currentJob) {
    return (
      <Layout>
        <div className="text-center py-24">
          <h2 className="record-title mb-4">Job not found</h2>
          <Button onClick={() => navigate('/jobs')}>Back to Jobs</Button>
        </div>
      </Layout>
    );
  }

  const actualLabour = daySheets.reduce((sum, s) => sum + (s.calculated?.day_total || 0), 0);
  const estimatedLabour = currentJob.totals?.labour || currentJob.totals?.labor || 0;
  
  // Calculate overtime total (OT + DT hours combined)
  // Get hourly rate from job phases if not in day sheet
  const overtimeTotal = daySheets.reduce((sum, s) => {
    const overtimeHours = s.calculated?.overtime_hours || 0;
    const doubleTimeHours = s.calculated?.double_time_hours || 0;
    
    // Try to get hourly rate from calculated field first
    let baseRate = s.calculated?.hourly_rate || 0;
    
    // If not available, calculate from job phases
    if (!baseRate && currentJob.phases && currentJob.phases.length > 0) {
      const sheetPhase = s.phase || 'production';
      const matchingPhase = currentJob.phases.find(p => p.type === sheetPhase);
      if (matchingPhase) {
        const dayRate = matchingPhase.day_rate || 0;
        const baseHours = matchingPhase.base_hours || 10; // Default to 10 hours
        baseRate = baseHours > 0 ? dayRate / baseHours : 0;
      }
    }
    
    return sum + (overtimeHours * baseRate * 1.5) + (doubleTimeHours * baseRate * 2);
  }, 0);

  // The client, then the shoot dates: under the title, or in the phone bar
  const shootDates = currentJob.shoot_dates || [];
  const jobSubtitle = [
    currentJob.client,
    shootDates.length > 0
      ? formatDate(shootDates[0]) + (shootDates.length > 1 ? ` - ${formatDate(shootDates[shootDates.length - 1])}` : '')
      : null,
  ].filter(Boolean).join(' · ');
  const currentStatus = STATUS_OPTIONS.find(s => s.value === currentJob.status);
  // The status picker: a pill on desktop, a cell of the phone panel
  const renderStatusSelect = (triggerClassName) => (
              <Select value={currentJob.status} onValueChange={handleStatusChange}>
                <SelectTrigger className={triggerClassName} data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <span className={`px-2.5 py-0.5 rounded-pill ${option.color}`}>{option.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
  );

  const isDone = currentJob.status === 'completed' || currentJob.status === 'paid';
  // Beside the status once a job is completed: when, and a way to change it
  const renderCompletedOn = (className) => (
    <DatePicker
      value={completionDay(currentJob.completed_at)}
      onChange={handleCompletedOnChange}
      clearable={false}
      placeholder="Completion date"
      region={regionConfig.code}
      className={className}
      title="Completion date"
      aria-label="Completion date"
      data-testid="completed-on"
    />
  );

  return (
    <Layout>
      <div className="space-y-6 animate-in" data-testid="job-detail-page">
        {/* Header */}
        <div>
          {/* Phone: back and title in the sticky bar instead */}
          <PhoneTitle title={currentJob.project_name} subtitle={jobSubtitle} onBack={() => navigate('/jobs')} backLabel="Back to Jobs" />
          <div className="title-row flex-wrap gap-3 max-sm:hidden">
            <BackButton onClick={() => navigate('/jobs')} label="Back to Jobs" />
            <h1 className="record-title">
              {currentJob.project_name}{currentJob.equipment_rental_only && currentJob.rental_start_date && currentJob.rental_end_date ? `: ${formatRentalPeriod(currentJob.rental_start_date, currentJob.rental_end_date)}` : ''}
            </h1>

            {currentJob.brief_id && (
              <Link
                to={`/briefs/${currentJob.brief_id}`}
                className="hidden sm:inline-flex items-center gap-1.5 h-9 px-4 rounded-pill bg-mist text-[15px] text-foreground hover:bg-parchment transition-colors"
                data-testid="job-linked-brief-link"
              >
                <FileText className="w-4 h-4" /> View brief
              </Link>
            )}
            {linkedTreatments.map((t) => (
              <Link
                key={t.id}
                to={`/treatments/${t.id}`}
                className="hidden sm:inline-flex items-center gap-1.5 h-9 px-4 rounded-pill bg-mist text-[15px] text-foreground hover:bg-parchment transition-colors"
                data-testid="job-linked-treatment-link"
              >
                <Clapperboard className="w-4 h-4" />
                {linkedTreatments.length > 1 ? `Shot list: ${t.title}` : 'View shot list'}
              </Link>
            ))}
          </div>
          {/* Under the title: the client, then the shoot dates. In the phone bar instead on a phone */}
          {jobSubtitle && (
            <p className="text-sm text-muted-foreground mt-1 ml-[52px] max-sm:hidden">{jobSubtitle}</p>
          )}
        </div>

        {/* Tabs with Action Buttons */}
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          {isPhone ? (
            <PhonePanel
              tabs={{
                value: tab,
                onChange: setTab,
                items: [
                  { value: 'overview', label: 'Overview', testId: 'tab-overview' },
                  ...(isAdmin ? [{ value: 'crew', label: 'Crew', testId: 'tab-crew' }] : []),
                  { value: 'daysheets', label: 'Day sheets', testId: 'tab-daysheets' },
                ],
              }}
              actions={[
                { key: 'status', node: renderStatusSelect(PHONE_CELL_CONTROL) },
                ...(isDone ? [{ key: 'completed-on', node: renderCompletedOn(PHONE_CELL_CONTROL) }] : []),
                {
                  label: 'Edit',
                  icon: Edit,
                  to: `/jobs/${jobId}/edit`,
                  primary: true,
                  // A completed or paid job is closed; desktop explains why in a tooltip
                  disabled: currentJob.status === 'completed' || currentJob.status === 'paid',
                  testId: 'edit-job-btn',
                },
              ]}
            />
          ) : (
          <PageToolbar className="sm:flex-row sm:items-center sm:justify-between">
            <div className="w-fit">
              <TabsList>
                <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
                {(isAdmin) && <TabsTrigger value="crew" data-testid="tab-crew">Crew / Suppliers</TabsTrigger>}
                <TabsTrigger value="daysheets" data-testid="tab-daysheets">Day sheets</TabsTrigger>
              </TabsList>
            </div>
            <div className="flex flex-wrap gap-2">
              {renderStatusSelect(`w-[150px] ${currentStatus?.color || ''} ${currentStatus?.borderColor || ''}`)}
              {isDone && renderCompletedOn('w-auto rounded-pill')}
              {isDone ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button 
                          variant="outline" 
                          disabled 
                          className="opacity-50 cursor-not-allowed"
                          data-testid="edit-job-btn"
                        >
                          <Edit className="w-4 h-4 mr-2" /> Edit
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Cannot edit {currentJob.status} jobs</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <Button asChild data-testid="edit-job-btn">
                  <Link to={`/jobs/${jobId}/edit`}>
                    <Edit className="w-4 h-4 mr-2" /> Edit
                  </Link>
                </Button>
              )}
              {/* Invoice button hidden - functionality preserved for future use */}
              {/* <Button variant="outline" onClick={handleDownloadInvoice} data-testid="download-invoice-btn">
                <Download className="w-4 h-4 mr-2" /> Invoice
              </Button> */}
            </div>
          </PageToolbar>
          )}

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6 mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Job Summary */}
              <Card className="lg:col-span-2">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                  <CardTitle>Job summary</CardTitle>
                  <Button variant="outline" size="sm" onClick={handleDownloadEstimate} data-testid="download-quote-btn">
                    <FileText className="w-4 h-4 mr-2" /> Print Estimate
                  </Button>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Job Description */}
                  {currentJob.description && (
                    <div>
                      <h4 className="eyebrow mb-3 ">Description</h4>
                      <div 
                        className="text-sm p-3 bg-secondary/50 rounded-md job-description-content"
                        dangerouslySetInnerHTML={{ __html: currentJob.description }}
                      />
                    </div>
                  )}

                  {/* Phases */}
                  {!currentJob.equipment_rental_only && currentJob.phases?.length > 0 && (
                    <div>
                      <h4 className="eyebrow mb-3 ">Labour</h4>
                      <div className="space-y-2">
                        {currentJob.phases.map((phase, index) => (
                          <div key={index} className="flex justify-between items-center p-3 bg-secondary/50 rounded-md">
                            <div>
                              <p className="font-medium capitalize">
                                {phase.type?.replace('_', ' ')}
                                {phase.label && (
                                  <>
                                    <span className="text-muted-foreground mx-1.5">|</span>
                                    <span className="text-muted-foreground font-normal">{phase.label}</span>
                                  </>
                                )}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {formatCurrency(phase.day_rate || 0)}/day × {phase.estimated_days} days
                              </p>
                            </div>
                            <p className="font-mono">{formatCurrency((phase.day_rate || 0) * (phase.estimated_days || 0))}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Crew Labour */}
                  {!currentJob.equipment_rental_only && currentJob.crew_labour?.length > 0 && (
                    <div>
                      <h4 className="eyebrow mb-3 ">Crew Labour</h4>
                      <div className="space-y-2">
                        {currentJob.crew_labour.map((crew, index) => (
                          <div key={index} className="flex justify-between items-center p-3 rounded-md bg-mist">
                            <div>
                              <p className="font-medium">
                                {crew.crew_name}
                                {crew.crew_role && (
                                  <>
                                    <span className="text-muted-foreground mx-1.5">|</span>
                                    <span className="text-muted-foreground font-normal">{crew.crew_role}</span>
                                  </>
                                )}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {formatCurrency(crew.client_rate || 0)}/{crew.day_type} × {crew.days} {crew.labour_rate_mode === 'hour' ? 'hrs' : 'days'}
                                {crew.start_date && ` • ${formatDate(crew.start_date)}`}
                              </p>
                            </div>
                            <p className="font-mono">{formatCurrency((crew.client_rate || 0) * (crew.days || 0))}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Equipment & Packages */}
                  {(currentJob.equipment?.length > 0 || currentJob.equipment_packages?.length > 0 || (!currentJob.equipment_rental_only && currentJob.crew_equipment?.length > 0)) && (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="eyebrow">Equipment</h4>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleDownloadEquipmentList}
                        >
                          <List className="w-4 h-4 mr-2" />
                          Print Equipment List
                        </Button>
                      </div>
                      <div className="space-y-2">
                        {(() => {
                          // Build display order from saved order or default
                          const packages = currentJob.equipment_packages || [];
                          const equipment = currentJob.equipment || [];
                          const crewEquipment = (!currentJob.equipment_rental_only && currentJob.crew_equipment) ? currentJob.crew_equipment : [];
                          const savedOrder = currentJob.equipment_display_order || [];
                          const packageEquipmentIds = new Set(packages.flatMap(pkg => pkg.equipment_ids || []));
                          
                          // Rate and package maths come from lib/jobFinancials, which
                          // is what the Job Builder and Job Edit pages use too. This
                          // page previously had its own copy whose budget branch set
                          // total = discount_value uncapped, so a package budget above
                          // its items showed a total the invoice never charged.
                          const getItemRate = getEquipmentItemRate;
                          const calculatePackageTotal = (pkg) => packageTotal(pkg, equipment);
                          
                          // Build ordered items
                          let orderedItems = [];
                          
                          if (savedOrder.length > 0) {
                            // Track which packages have been matched to avoid duplicates
                            const matchedPackageIndices = new Set();
                            
                            // Use saved order
                            savedOrder.forEach(orderItem => {
                              if (orderItem.type === 'package') {
                                // Try to match by id first (new format), then equipment_ids_key, then name (legacy)
                                let pkgIndex = -1;
                                if (orderItem.id) {
                                  pkgIndex = packages.findIndex((p, idx) => p.id === orderItem.id && !matchedPackageIndices.has(idx));
                                }
                                if (pkgIndex === -1 && orderItem.equipment_ids_key) {
                                  pkgIndex = packages.findIndex((p, idx) => {
                                    const pkgKey = p.equipment_ids ? [...p.equipment_ids].sort().join(',') : '';
                                    return pkgKey === orderItem.equipment_ids_key && !matchedPackageIndices.has(idx);
                                  });
                                }
                                // Fall back to name matching for legacy data
                                if (pkgIndex === -1 && orderItem.name) {
                                  pkgIndex = packages.findIndex((p, idx) => p.name === orderItem.name && !matchedPackageIndices.has(idx));
                                }
                                if (pkgIndex !== -1) {
                                  matchedPackageIndices.add(pkgIndex);
                                  orderedItems.push({ type: 'package', data: packages[pkgIndex] });
                                }
                              } else if (orderItem.type === 'equipment') {
                                // Try to match by id first (new format), then inventory_id (legacy)
                                let eq = null;
                                if (orderItem.id) {
                                  eq = equipment.find(e => e.id === orderItem.id);
                                }
                                if (!eq && orderItem.inventory_id) {
                                  eq = equipment.find(e => e.inventory_id === orderItem.inventory_id);
                                }
                                if (eq && !packageEquipmentIds.has(eq.inventory_id)) {
                                  orderedItems.push({ type: 'equipment', data: eq });
                                }
                              } else if (orderItem.type === 'divider') {
                                orderedItems.push({ type: 'divider', data: { label: orderItem.label || '' } });
                              } else if (orderItem.type === 'crew_equipment') {
                                const ce = crewEquipment.find(c => c.id === orderItem.crew_equipment_id);
                                if (ce) orderedItems.push({ type: 'crew_equipment', data: ce });
                              }
                            });
                            // Add any items not in saved order
                            packages.forEach((pkg, idx) => {
                              if (!matchedPackageIndices.has(idx)) {
                                orderedItems.push({ type: 'package', data: pkg });
                              }
                            });
                            equipment.forEach(eq => {
                              if (!packageEquipmentIds.has(eq.inventory_id) && 
                                  !orderedItems.find(o => o.type === 'equipment' && o.data.inventory_id === eq.inventory_id)) {
                                orderedItems.push({ type: 'equipment', data: eq });
                              }
                            });
                            crewEquipment.forEach(ce => {
                              if (!orderedItems.find(o => o.type === 'crew_equipment' && o.data === ce)) {
                                orderedItems.push({ type: 'crew_equipment', data: ce });
                              }
                            });
                          } else {
                            // Default order: packages first, then equipment, then crew equipment
                            packages.forEach(pkg => orderedItems.push({ type: 'package', data: pkg }));
                            equipment.forEach(eq => {
                              if (!packageEquipmentIds.has(eq.inventory_id)) {
                                orderedItems.push({ type: 'equipment', data: eq });
                              }
                            });
                            crewEquipment.forEach(ce => orderedItems.push({ type: 'crew_equipment', data: ce }));
                          }
                          
                          return orderedItems.map((item, index) => {
                            if (item.type === 'divider') {
                              return (
                                <div key={`div-${index}`} className="flex items-center gap-3 pt-3 pb-1" data-testid="overview-equipment-divider">
                                  <span className="text-sm font-medium text-foreground whitespace-nowrap">{item.data.label}</span>
                                  <div className="h-px flex-1 bg-border" />
                                </div>
                              );
                            }
                            if (item.type === 'crew_equipment') {
                              const ce = item.data;
                              return (
                                <div key={`crew-${index}`} className="flex justify-between items-center p-3 rounded-md border-l-4 border-line-strong bg-mist" data-testid="overview-crew-equipment">
                                  <div>
                                    <p className="font-medium text-foreground">
                                      {ce.crew_name}
                                      {ce.description && (
                                        <>
                                          <span className="mx-1.5 opacity-50">|</span>
                                          <span className="font-normal opacity-70">{ce.description}</span>
                                        </>
                                      )}
                                    </p>
                                    <p className="text-sm text-foreground opacity-70">
                                      {formatCurrency(ce.client_rate || 0)} × {ce.quantity} × {ce.days} days
                                    </p>
                                  </div>
                                  <p className="font-mono text-foreground">{formatCurrency((ce.client_rate || 0) * (ce.quantity || 1) * (ce.days || 0))}</p>
                                </div>
                              );
                            }
                            if (item.type === 'package') {
                              const pkg = item.data;
                              const { subtotal, discount, total, itemsInPackage } = calculatePackageTotal(pkg);
                              const hasDiscount = pkg.discount_value > 0;
                              
                              const getDiscountLabel = () => {
                                if (pkg.discount_type === 'percentage') return `${pkg.discount_value}% off`;
                                if (pkg.discount_type === 'budget') return `Budget: ${formatCurrency(pkg.discount_value)}`;
                                return `${formatCurrency(pkg.discount_value)} off`;
                              };
                              
                              return (
                                <div key={`pkg-${index}`} className="p-3 bg-secondary/50 rounded-md border-l-4 border-primary">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <Package className="w-4 h-4 text-primary" />
                                      <span className="font-medium">{pkg.name}</span>
                                      {hasDiscount && (
                                        <span className="text-xs text-muted-foreground px-2 py-0.5 bg-primary/10 rounded">
                                          {getDiscountLabel()}
                                        </span>
                                      )}
                                    </div>
                                    <span className="font-mono font-medium">{formatCurrency(total)}</span>
                                  </div>
                                  <div className="space-y-1 pl-6 mb-2">
                                    {itemsInPackage.map((eqItem, eqIndex) => (
                                      <div key={eqIndex} className="flex justify-between text-sm text-muted-foreground">
                                        <span>{eqItem.name}</span>
                                        <span className="font-mono">
                                          {formatCurrency(getItemRate(eqItem) * eqItem.scheduled_days * eqItem.quantity)}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                  {hasDiscount && (
                                    <div className="pl-6 pt-2 border-t border-border/50 space-y-1">
                                      <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Subtotal</span>
                                        <span className="font-mono line-through text-muted-foreground">{formatCurrency(subtotal)}</span>
                                      </div>
                                      <div className="flex justify-between text-sm text-primary">
                                        <span>Package Discount</span>
                                        <span className="font-mono">-{formatCurrency(discount)}</span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            } else {
                              const eq = item.data;
                              const eqRate = eq.rate_mode === 'week'
                                ? ((eq.rate_override !== null && eq.rate_override !== undefined ? eq.rate_override : eq.weekly_rate) || 0)
                                : ((eq.rate_override !== null && eq.rate_override !== undefined ? eq.rate_override : eq.daily_rate) || 0);
                              const rateLabel = eq.rate_mode === 'week' ? 'wk' : 'day';
                              const durationLabel = eq.rate_mode === 'week'
                                ? `${eq.scheduled_days} wk${eq.scheduled_days !== 1 ? 's' : ''}`
                                : `${eq.scheduled_days} day${eq.scheduled_days !== 1 ? 's' : ''}`;
                              return (
                                <div key={`eq-${index}`} className="p-3 bg-secondary/50 rounded-md">
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <p className="font-medium">{eq.name}</p>
                                      <p className="text-sm text-muted-foreground">
                                        {eq.quantity}x × {formatCurrency(eqRate)}/{rateLabel} × {durationLabel}
                                      </p>
                                    </div>
                                    <p className="font-mono">
                                      {formatCurrency(eqRate * eq.scheduled_days * eq.quantity)}
                                    </p>
                                  </div>
                                  {eq.description && (
                                    <p className="text-sm text-muted-foreground mt-2 pl-2 border-l-2 border-border whitespace-pre-line">
                                      {eq.description}
                                    </p>
                                  )}
                                </div>
                              );
                            }
                          });
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Expenses */}
                  {currentJob.expenses?.length > 0 && (
                    <div>
                      <h4 className="eyebrow mb-3 ">Expenses</h4>
                      <div className="space-y-2">
                        {currentJob.expenses.map((expense, index) => (
                          <div key={index} className="flex justify-between items-center p-3 bg-secondary/50 rounded-md">
                            <div>
                              <p className="font-medium">{expense.description || 'Expense'}</p>
                              <p className="text-sm text-muted-foreground">
                                {formatCurrency(expense.amount || 0)} × {expense.quantity || 1}
                              </p>
                            </div>
                            <p className="font-mono">{formatCurrency((expense.amount || 0) * (expense.quantity || 1))}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Hidden Costs - not included in estimates/invoices */}
                  {currentJob.hidden_costs?.length > 0 && (
                    <div>
                      <h4 className="eyebrow mb-3 ">Hidden Costs</h4>
                      <p className="text-xs text-muted-foreground mb-2 italic">Not included in estimates or invoices</p>
                      <div className="space-y-2">
                        {currentJob.hidden_costs.map((cost, index) => (
                          <div key={index} className="flex justify-between items-center p-3 bg-secondary/50 rounded-md border-l-2 border-ochre/50">
                            <div>
                              <p className="font-medium">{cost.description || 'Hidden Cost'}</p>
                              <p className="text-sm text-muted-foreground">
                                {formatCurrency(cost.amount || 0)} × {cost.quantity || 1}
                              </p>
                            </div>
                            <p className="font-mono">{formatCurrency((cost.amount || 0) * (cost.quantity || 1))}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Crew Superannuation */}
                  {(() => {
                    // The backend's figure, persisted with job.totals - and absent
                    // outside Australia. Not recomputed here.
                    const crewSuperTotal = Number(currentJob.crew_superannuation?.total_amount) || 0;

                    if (crewSuperTotal === 0) return null;
                    
                    return (
                      <div>
                        <h4 className="eyebrow mb-3 ">Crew Superannuation</h4>
                        <div className="p-3 rounded-md bg-mist">
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="font-medium">Superannuation</p>
                              <p className="text-sm text-muted-foreground">
                                {currentJob.crew_superannuation.percentage}% of crew labour
                              </p>
                            </div>
                            <p className="font-mono">{formatCurrency(crewSuperTotal)}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>

              {/* Totals Sidebar */}
              <div className="space-y-4">
                {/* Job reference: the job's numbers, the client's own references (editable
                    in place) and the Xero action, in the same rows as the panels below */}
                {(() => {
                  const sendToXero = async () => {
                    setSendingToXero(true);
                    const toastId = toast.loading(currentJob.xero_invoice_id ? 'Updating Xero invoice...' : 'Sending to Xero...');
                    try {
                      const result = await sendJobToXero(jobId);
                      toast.success(result.message || 'Invoice sent to Xero!', { id: toastId });
                      await fetchJob(jobId);
                    } catch (err) {
                      const message = err.response?.data?.detail || 'Failed to send to Xero';
                      toast.error(message, { id: toastId });
                    } finally {
                      setSendingToXero(false);
                    }
                  };
                  // Plain helpers, not components: a component made in here would be new on
                  // every render, remounting the edit field and losing focus on each keystroke
                  const row = (label, children) => (
                    <div className="flex items-center justify-between gap-3 border-t border-border pt-3 text-[15px] min-h-[36px]">
                      <span className="text-muted-foreground shrink-0">{label}</span>
                      <div className="min-w-0 text-right">{children}</div>
                    </div>
                  );
                  // A client reference: click to edit in place, Enter to save, Escape to cancel
                  const editable = (field, value, placeholder) => (
                    editingField === field ? (
                      <div className="flex items-center gap-1 justify-end">
                        <Input
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveInlineEdit();
                            if (e.key === 'Escape') cancelEditing();
                          }}
                          className="h-9 text-sm w-44"
                          autoFocus
                          placeholder={placeholder}
                        />
                        <Button variant="ghost" size="icon" className="h-9 w-9" onClick={saveInlineEdit} aria-label="Save">
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-9 w-9" onClick={cancelEditing} aria-label="Cancel">
                          <X className="w-4 h-4 text-muted-foreground" />
                        </Button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="group inline-flex items-center gap-1.5 max-w-full"
                        onClick={() => startEditing(field, value)}
                      >
                        <span className={`truncate ${value ? 'font-medium' : 'text-muted-foreground italic'}`}>{value || 'Add'}</span>
                        <Pencil className="w-3.5 h-3.5 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity" />
                      </button>
                    )
                  );

                  return (
                    <Card>
                      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0 pb-2">
                        <CardTitle>Job reference</CardTitle>
                          {xeroStatus.connected ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={sendToXero}
                              disabled={sendingToXero}
                              data-testid="send-to-xero-btn"
                            >
                              {sendingToXero ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
                              {currentJob.xero_invoice_id ? 'Update Xero' : 'Send to Xero'}
                            </Button>
                          ) : (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    // Connecting is done from Settings, where the connection is managed
                                    onClick={() => navigate('/settings?tab=xero')}
                                    data-testid="connect-xero-btn"
                                  >
                                    <FileSpreadsheet className="w-4 h-4" /> Connect Xero
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Connect your Xero account to send invoices directly</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {row('Job number', <span className="font-medium tabular-nums">{currentJob.job_number || currentJob.id?.slice(0, 8).toUpperCase()}</span>)}
                        {currentJob.xero_invoice_number && (
                          row('Xero invoice', <span className="font-medium tabular-nums">{currentJob.xero_invoice_number}</span>)
                        )}
                        {row('Client job name', editable('client_job_name', currentJob.client_job_name, "Client's name for this job"))}
                        {row('Client reference', editable('client_job_number', currentJob.client_job_number, "e.g., PO-12345"))}

                      </CardContent>
                    </Card>
                  );
                })()}

                {/* The totals: this screen's one inverse band */}
                <Card className="bg-inverse text-inverse-foreground rounded-highlight">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                    <CardTitle className="flex flex-col gap-1">
                      <span className="eyebrow text-inverse-muted">totals</span>
                      <span>Summary</span>
                    </CardTitle>
                    <Button variant="outline" size="sm" className="border-inverse-muted text-inverse-foreground hover:bg-inverse-foreground/10 hover:text-inverse-foreground" onClick={handleDownloadXeroCSV} data-testid="download-xero-csv-btn">
                      <FileSpreadsheet className="w-4 h-4 mr-1" /> Export CSV
                    </Button>
                  </CardHeader>
                    <CardContent className="space-y-3">
                      {/* Summary Only Toggle */}
                      <div className="flex items-center justify-between pb-3 border-b border-inverse-muted/30">
                        <div className="flex flex-col">
                          <Label htmlFor="summary-only-toggle" className="text-sm font-medium">Summary only</Label>
                          <p className="text-sm text-inverse-muted">Show totals only in PDF/Xero</p>
                        </div>
                        <Switch 
                          id="summary-only-toggle"
                          checked={currentJob.summary_only || false}
                          onCheckedChange={handleSummaryOnlyToggle}
                          className="data-[state=checked]:bg-inverse-foreground data-[state=unchecked]:bg-inverse-muted/40 [&>span]:bg-inverse"
                          data-testid="summary-only-toggle"
                        />
                      </div>
                      
                      {!currentJob.equipment_rental_only && (
                      <div className="flex justify-between text-sm">
                        <span className="text-inverse-muted">Labour</span>
                        <span>{formatCurrency(estimatedLabour)}</span>
                      </div>
                      )}
                      {!currentJob.equipment_rental_only && overtimeTotal> 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-inverse-muted">Overtime</span>
                          <span className="text-ochre-soft">{formatCurrency(overtimeTotal)}</span>
                        </div>
                      )}
                      {(() => {
                        // From the shared model (lib/jobFinancials), the same figures the job form shows
                        const { equipmentSubtotal, packageDiscountTotal } = computeEquipmentTotals(currentJob);
                        return (
                          <>
                            <div className="flex justify-between text-sm">
                              <span className="text-inverse-muted">Equipment</span>
                              <span>{formatCurrency(equipmentSubtotal)}</span>
                            </div>
                            {(currentJob.totals?.expenses > 0 || currentJob.expenses?.length > 0) && (
                              <div className="flex justify-between text-sm">
                                <span className="text-inverse-muted">Expenses</span>
                                <span>{formatCurrency(currentJob.totals?.expenses || 0)}</span>
                              </div>
                            )}
                            {packageDiscountTotal > 0 && (
                              <div className="flex justify-between text-sm">
                                <span>Package discounts</span>
                                <span>-{formatCurrency(packageDiscountTotal)}</span>
                              </div>
                            )}
                          </>
                        );
                      })()}
                      <div className="border-t border-inverse-muted/30 pt-3">
                        {(() => {
                          // Use job's pre-calculated totals (which include crew costs)
                          const actualSubtotal = currentJob.totals?.subtotal || 0;
                          const actualGst = currentJob.totals?.gst || 0;
                          const actualGrand = currentJob.totals?.grand || 0;
                          return (
                            <>
                              <div className="flex justify-between text-sm">
                                <span className="text-inverse-muted">Subtotal</span>
                                <span>{formatCurrency(actualSubtotal)}</span>
                              </div>
                              {currentJob.include_gst && (
                                <div className="flex justify-between text-sm mt-2">
                                  <span className="text-inverse-muted">{getTaxName()} ({currentJob.gst_rate || regionConfig.tax.defaultRate}%)</span>
                                  <span>{formatCurrency(actualGst)}</span>
                                </div>
                              )}
                              <div className="mt-4 flex flex-col gap-1">
                                <span className="eyebrow text-inverse-muted">grand total</span>
                                <span className="text-5xl font-light tracking-tight leading-none" data-testid="grand-total">
                                  {formatCurrency(actualGrand)}
                                </span>
                              </div>
                              {currentJob.include_gst && (
                                <p className="text-sm text-inverse-muted mt-1">Inc. {getTaxName()}</p>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </CardContent>
                  </Card>

                {/* Profit: the answer first, then how it's made up */}
                {daySheets.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle>Profit analysis</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {(() => {
                        // All figures come from the shared financial model so this
                        // card can never drift from the dashboard, kanban or reports.
                        // See src/lib/jobFinancials.js - do not recompute here.
                        const {
                          jobValue: totalJobValue,
                          generalExpenses,
                          hiddenCostsTotal,
                          crewLabourBaseCost,
                          crewEquipmentBaseCost,
                          crewSuperCost,
                          totalCrewBaseCosts,
                          crewLabourMargin,
                          crewEquipmentMargin,
                          totalCrewMargin,
                          profit: yourNetProfit,
                          profitMargin,
                        } = computeJobFinancials(currentJob);
                        const positive = yourNetProfit >= 0;
                        // One line of the breakdown: label left, figure right
                        const Line = ({ label, value, tone, sub = false, strong = false }) => (
                          <div className={`flex justify-between gap-3 ${sub ? 'pl-4 text-sm text-muted-foreground' : 'text-[15px]'} ${strong ? 'font-medium' : ''}`}>
                            <span>{label}</span>
                            <span className={`tabular-nums ${tone || ''}`}>{value}</span>
                          </div>
                        );

                        return (
                          <>
                            <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-1">
                              <div>
                                <p className="eyebrow">your net profit</p>
                                <p className={`text-4xl xl:text-5xl font-light tracking-tight leading-none tabular-nums mt-1 ${positive ? 'text-moss' : 'text-brick'}`}>
                                  {formatCurrency(yourNetProfit)}
                                </p>
                              </div>
                              <p className={`text-sm tabular-nums pb-1 ${profitMargin >= 0 ? 'text-moss' : 'text-brick'}`}>
                                {profitMargin.toFixed(1)}% margin
                              </p>
                            </div>

                            <div className="space-y-2 border-t border-border pt-4">
                              <Line label="Job value (excl. tax)" value={formatCurrency(totalJobValue)} />
                            </div>

                            {(generalExpenses > 0 || hiddenCostsTotal > 0 || totalCrewBaseCosts > 0) && (
                              <div className="space-y-2 border-t border-border pt-4">
                                <p className="eyebrow">less costs</p>
                                {generalExpenses > 0 && (
                                  <Line label="Expenses" value={`-${formatCurrency(generalExpenses)}`} tone="text-brick" />
                                )}
                                {hiddenCostsTotal > 0 && (
                                  <Line label="Hidden costs" value={`-${formatCurrency(hiddenCostsTotal)}`} tone="text-brick" />
                                )}
                                {totalCrewBaseCosts > 0 && (
                                  <>
                                    <Line label="Crew" value={`-${formatCurrency(totalCrewBaseCosts)}`} tone="text-brick" />
                                    {crewLabourBaseCost > 0 && <Line sub label="Labour" value={formatCurrency(crewLabourBaseCost)} />}
                                    {crewEquipmentBaseCost > 0 && <Line sub label="Equipment" value={formatCurrency(crewEquipmentBaseCost)} />}
                                    {crewSuperCost > 0 && <Line sub label="Superannuation" value={formatCurrency(crewSuperCost)} />}
                                  </>
                                )}
                              </div>
                            )}

                            {totalCrewMargin > 0 && (
                              <div className="space-y-2 border-t border-border pt-4">
                                <p className="eyebrow">crew markup kept</p>
                                {crewLabourMargin > 0 && (
                                  <Line label="Labour" value={`+${formatCurrency(crewLabourMargin)}`} tone="text-moss" />
                                )}
                                {crewEquipmentMargin > 0 && (
                                  <Line label="Equipment" value={`+${formatCurrency(crewEquipmentMargin)}`} tone="text-moss" />
                                )}
                                <Line strong label="Total" value={`+${formatCurrency(totalCrewMargin)}`} tone="text-moss" />
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </CardContent>
                  </Card>
                )}

                <Button 
                  variant="outline" 
                  className="w-full" 
                  onClick={handleDuplicate}
                  disabled={duplicating}
                  data-testid="duplicate-job-btn"
                >
                  {duplicating ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Copy className="w-4 h-4 mr-2" />
                  )}
                  Duplicate job
                </Button>
                <Button variant="destructive" className="w-full" onClick={handleDelete} data-testid="delete-job-btn">
                  <Trash2 className="w-4 h-4 mr-2" /> Delete Job
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Crew Tab */}
          {(isAdmin) && (
          <TabsContent value="crew" className="space-y-6 mt-6">
            <CrewTab key={currentJob?.updated_at} job={currentJob} token={token} formatCurrency={formatCurrency} formatDate={formatDate} xeroStatus={xeroStatus} />
          </TabsContent>
          )}

          {/* Day Sheets Tab */}
          <TabsContent value="daysheets" className="space-y-6">
            {/* Day sheets now auto-sync with job estimate. Use Edit Job button to manage phases/days */}

            {/* Day Sheet Dialog */}
            <Dialog open={isSheetDialogOpen} onOpenChange={setIsSheetDialogOpen}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editingSheetId ? 'Edit Day Sheet' : 'New Day Sheet'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSaveDaySheet} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="sheet-date">Date</Label>
                    <DatePicker
                      value={sheetForm.date}
                      onChange={(value) => setSheetForm({ ...sheetForm, date: value })}
                      placeholder="Select date"
                      data-testid="daysheet-date-input"
                      region={regionConfig.code}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Phase</Label>
                      <Select
                        value={sheetForm.phase}
                        onValueChange={handlePhaseChange}
                      >
                        <SelectTrigger data-testid="daysheet-phase-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pre">Pre-Production</SelectItem>
                          <SelectItem value="production">Production</SelectItem>
                          <SelectItem value="post">Post-Production</SelectItem>
                          <SelectItem value="travel">Travel</SelectItem>
                          <SelectItem value="wet_weather">Wet Weather Hold</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="sheet-label">Custom Label <span className="text-muted-foreground text-xs">(optional)</span></Label>
                      <Input
                        id="sheet-label"
                        type="text"
                        value={sheetForm.label || ''}
                        onChange={(e) => setSheetForm({ ...sheetForm, label: e.target.value })}
                        placeholder="e.g., Main Shoot"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="call-time">Call Time <span className="text-muted-foreground text-xs">(optional)</span></Label>
                      <Input
                        id="call-time"
                        type="time"
                        value={sheetForm.call_time}
                        onChange={(e) => setSheetForm({ ...sheetForm, call_time: e.target.value })}
                        data-testid="daysheet-call-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="wrap-time">Wrap Time <span className="text-muted-foreground text-xs">(optional)</span></Label>
                      <Input
                        id="wrap-time"
                        type="time"
                        value={sheetForm.wrap_time}
                        onChange={(e) => setSheetForm({ ...sheetForm, wrap_time: e.target.value })}
                        data-testid="daysheet-wrap-input"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Leave call/wrap times empty to use the phase day rate instead of calculated hours.
                  </p>
                  
                  {/* Breaks */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Breaks</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addBreak}>
                        <Plus className="w-3 h-3 mr-1" /> Add Break
                      </Button>
                    </div>
                    {sheetForm.breaks.map((brk, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 bg-secondary/50 rounded-md">
                        <Select
                          value={brk.type}
                          onValueChange={(value) => updateBreak(index, 'type', value)}
                        >
                          <SelectTrigger className="w-24 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="lunch">Lunch</SelectItem>
                            <SelectItem value="dinner">Dinner</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          type="time"
                          value={brk.out_time}
                          onChange={(e) => updateBreak(index, 'out_time', e.target.value)}
                          className="w-28 h-8"
                        />
                        <span className="text-muted-foreground">to</span>
                        <Input
                          type="time"
                          value={brk.in_time}
                          onChange={(e) => updateBreak(index, 'in_time', e.target.value)}
                          className="w-28 h-8"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => removeBreak(index)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  
                  <Button type="submit" className="w-full" data-testid="save-daysheet-btn">
                    <Save className="w-4 h-4 mr-2" />
                    {editingSheetId ? 'Save Changes' : 'Create Day Sheet'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            {daySheets.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Clock className="w-12 h-12 text-muted-foreground/50 mb-4" />
                  <h3 className="text-lg font-medium mb-2">No time tracked yet</h3>
                  <p className="text-muted-foreground mb-4">Add day sheets to track your hours</p>
                </CardContent>
              </Card>
            ) : (
              <Card className="mt-6 rounded-cards overflow-hidden">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead className="hidden sm:table-cell">Phase</TableHead>
                        <TableHead className="hidden md:table-cell">Call</TableHead>
                        <TableHead className="hidden md:table-cell">Wrap</TableHead>
                        <TableHead className="hidden lg:table-cell text-right">Billable</TableHead>
                        <TableHead className="hidden lg:table-cell text-right">OT</TableHead>
                        <TableHead className="hidden lg:table-cell text-right">2T</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="w-[70px] sm:w-[80px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {daySheets.map((sheet) => {
                        const useDayRate = !sheet.call_time || !sheet.wrap_time;
                        return (
                          <TableRow 
                            key={sheet.id} 
                            data-testid={`daysheet-row-${sheet.id}`}
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => openEditSheet(sheet)}
                          >
                            <TableCell>
                              <div>
                                <p className="font-medium">
                                  {sheet.date ? formatDateDMY(sheet.date) : <span className="text-muted-foreground italic">{sheet.notes || 'Unscheduled'}</span>}
                                </p>
                                {/* Mobile: show phase and times below date */}
                                <div className="sm:hidden text-xs text-muted-foreground mt-0.5">
                                  <span className="capitalize">{getPhaseDisplayName(sheet.phase, sheet.label)}</span>
                                  {sheet.call_time && sheet.wrap_time && (
                                    <span> · {sheet.call_time} - {sheet.wrap_time}</span>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="hidden sm:table-cell capitalize">{getPhaseDisplayName(sheet.phase, sheet.label)}</TableCell>
                            <TableCell className="hidden md:table-cell font-mono text-sm">{sheet.call_time || <span className="text-muted-foreground">-</span>}</TableCell>
                            <TableCell className="hidden md:table-cell font-mono text-sm">{sheet.wrap_time || <span className="text-muted-foreground">-</span>}</TableCell>
                            <TableCell className="hidden lg:table-cell text-right font-mono text-sm">
                              {useDayRate ? <span className="text-muted-foreground">Day Rate</span> : `${sheet.calculated?.billable_hours?.toFixed(1) || 0} hrs`}
                            </TableCell>
                            <TableCell className="hidden lg:table-cell text-right font-mono text-sm text-ochre">
                              {useDayRate ? '-' : `${sheet.calculated?.overtime_hours?.toFixed(1) || 0} hrs`}
                            </TableCell>
                            <TableCell className="hidden lg:table-cell text-right font-mono text-sm text-brick">
                              {useDayRate ? '-' : `${sheet.calculated?.double_time_hours?.toFixed(1) || 0} hrs`}
                            </TableCell>
                            <TableCell className="text-right font-mono font-medium">
                              {formatCurrency(sheet.calculated?.day_total || 0)}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={(e) => { e.stopPropagation(); openEditSheet(sheet); }}
                                  data-testid={`edit-daysheet-${sheet.id}`}
                                >
                                  <Edit className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={(e) => { e.stopPropagation(); handleDeleteDaySheet(sheet.id); }}
                                  data-testid={`delete-daysheet-${sheet.id}`}
                                >
                                  <Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </Card>
            )}

            {daySheets.length > 0 && (
              <Card>
                <CardContent className="p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Tracked</p>
                      <p className="text-lg font-mono">
                        {daySheets.reduce((sum, s) => sum + (s.calculated?.billable_hours || 0), 0).toFixed(1)} hours
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Labour Total</p>
                      <p className="text-3xl font-light tracking-tight">
                        {formatCurrency(actualLabour)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Lost Reason Dialog */}
      <Dialog open={lostReasonDialogOpen} onOpenChange={setLostReasonDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="lost-reason-dialog">
          <DialogHeader>
            <DialogTitle>Mark Job as Lost</DialogTitle>
            <DialogDescription>
              Optionally provide a reason why this job was lost. This helps with future reference.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="lost-reason">Reason (optional)</Label>
              <Textarea
                id="lost-reason"
                placeholder="e.g., Out of budget, Not awarded, Client postponed..."
                value={lostReason}
                onChange={(e) => setLostReason(e.target.value)}
                className="min-h-[100px]"
                data-testid="lost-reason-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setLostReasonDialogOpen(false);
                setLostReason('');
              }}
              data-testid="lost-reason-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleLostReasonSubmit}
              className="bg-brick text-on-danger hover:bg-brick/90"
              data-testid="lost-reason-submit"
            >
              Mark as Lost
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
